"use client";

import ScreenLayout from "@/components/layout/screen-layout";
import { Scroll_Card_Section_Easy_Follow } from "@/lib/dto/home/easy-follow/easy-follow-data";
import { cn } from "@/lib/utils";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Image from "next/image";
import React, { useEffect, useRef } from "react";
import HeadingSectionEasyFollow from "./heading-section-easy-follow";

gsap.registerPlugin(ScrollTrigger);

function ScrollCardSectionEasyFollow() {
  const sectionRef = useRef(null);
  const boxRefs = useRef<HTMLDivElement[]>([]);
  const textRefs = useRef<HTMLDivElement[]>([]);

  const totalSteps = Scroll_Card_Section_Easy_Follow.length;
  // Responsive scroll length per step
  const getScrollLengthPerStep = () => {
    if (typeof window !== 'undefined') {
      return Math.max(250, window.innerHeight * 0.6);
    }
    return 250;
  };
  const [scrollLengthPerStep, setScrollLengthPerStep] = React.useState(getScrollLengthPerStep());
  // const totalScrollLength = totalSteps * scrollLengthPerStep;

  // Update scroll length on resize
  React.useEffect(() => {
    const handleResize = () => {
      setScrollLengthPerStep(getScrollLengthPerStep());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // useEffect(() => {
  //   // Disable right-click
  //   document.addEventListener("contextmenu", (e) => e.preventDefault());

  //   // Block keys
  //   const handler = (e: KeyboardEvent) => {
  //     if (
  //       e.key === "F12" ||
  //       (e.ctrlKey && e.shiftKey && e.key === "I") ||
  //       (e.metaKey && e.altKey && e.key === "I") ||
  //       (e.ctrlKey && e.key === "u") ||
  //       (e.metaKey && e.key === "u")
  //     ) {
  //       e.preventDefault();
  //     }
  //   };
  //   document.addEventListener("keydown", handler);

  //   return () => {
  //     document.removeEventListener("contextmenu", (e) => e.preventDefault());
  //     document.removeEventListener("keydown", handler);
  //   };
  // }, []);

  useEffect(() => {
    // Only enable ScrollTrigger on desktop
    if (typeof window === 'undefined' || window.innerWidth < 768) return;
    const headerHeight = 60;
    const trigger = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: `top-=${headerHeight} top`,
      end: `+=${totalSteps * scrollLengthPerStep}`,
      scrub: true,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      refreshPriority: -1,
      // markers: true, // Uncomment for debug
      onUpdate: (self) => {
        const rawIndex = Math.floor(self.progress * totalSteps);
        const index = Math.min(totalSteps - 1, Math.max(0, rawIndex));

        boxRefs.current.forEach((box, i) => {
          box.classList.toggle("opacity-100", i === index);
          box.classList.toggle("opacity-0", i !== index);
          box.classList.toggle("scale-100", i === index);
          box.classList.toggle("scale-95", i !== index);
          box.classList.toggle("translate-y-0", i === index);
          box.classList.toggle("translate-y-2", i !== index);
          box.classList.toggle("z-10", i === index);
          box.classList.toggle("z-0", i !== index);
        });

        textRefs.current.forEach((text, i) => {
          text.classList.toggle("opacity-100", i === index);
          text.classList.toggle("opacity-40", i !== index);
          // text.classList.toggle("scale-105", i === index);
          // text.classList.toggle("scale-100", i !== index);
          text.classList.toggle("-translate-y-2", i === index);
          text.classList.toggle("translate-y-0", i !== index);
          // text.classList.toggle("shadow-lg", i === index);
          // text.classList.toggle("shadow-none", i !== index);
          text.classList.toggle("text-black", i === index);
          text.classList.toggle("text-gray-400", i !== index);
        });
      },
    });

    return () => {
      trigger.kill();
    };
  }, [scrollLengthPerStep, totalSteps]);

  return (
    <section ref={sectionRef} className="relative md:h-auto overflow-hidden will-change-transform" style={{ transform: 'translate3d(0,0,0)' }}>
      <HeadingSectionEasyFollow />

      {/* Desktop Version */}
      <ScreenLayout className="lg:flex hidden will-change-transform">
        <div className="flex justify-center items-center pt-12 md:pt-13 gap-x-15 mx-auto will-change-transform">
          <div className="w-149.25 h-135 rounded-2xl bg-gray-200 relative will-change-transform">
            {Scroll_Card_Section_Easy_Follow.map((v, i) => (
              <Image
                key={i}
                ref={(el) => {
                  if (el) boxRefs.current[i] = el;
                }}
                src={v.img}
                alt="kick off plan"
                className={cn(
                  "absolute inset-0 transition-all duration-500 ease-in-out will-change-transform",
                  i === 0
                    ? "opacity-100 scale-100 translate-y-0 z-10"
                    : "opacity-0 scale-95 translate-y-2 z-0"
                )}
              />
            ))}
          </div>
          <div className="flex flex-col justify-center items-center gap-y-8">
            {Scroll_Card_Section_Easy_Follow.map((v, i) => (
              <div
                key={i}
                ref={(el) => {
                  if (el) textRefs.current[i] = el;
                }}
                className={cn(
                  "transition-all duration-500 transform max-w-md pl-4.5 origin-left relative",
                  i === 0
                    ? "opacity-100 scale-105 -translate-y-2  text-black"
                    : "opacity-40 scale-100 translate-y-0  text-gray-400"
                )}
              >
                <div className="absolute left-0 top-1 bottom-0 w-1 rounded-full bg-primary" />
                <h2 className="text-xl font-bold text-primary flex items-center gap-1">
                  {v.title}
                </h2>
                <div className="pt-3">
                  <p className="italic text-base  text-custom-blue opacity-60">
                    {v.sub_title}
                  </p>
                  <p className="text-custom-blue text-base font-normal">
                    {v.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ScreenLayout>

      {/* Mobile Version */}
      <div className="lg:hidden flex flex-col px-4 py-8 space-y-10 md:px-8 md:py-12">
        <div className="md:grid md:grid-cols-2 md:gap-8 md:space-y-0 space-y-10">
          {Scroll_Card_Section_Easy_Follow.map((v, i) => (
            <div key={i} className="flex flex-col items-center space-y-5 bg-white rounded-xl shadow-md p-4 pb-6 md:p-5 md:pb-7 md:shadow-lg">
              <div className="w-full max-w-sm h-72 rounded-xl bg-gray-200 relative overflow-hidden shadow-sm md:h-80">
                <Image
                  src={v.img}
                  alt={v.title}
                  className="absolute inset-0 w-full h-full object-cover rounded-xl md:rounded-2xl"
                />
              </div>
              <div className="text-center max-w-sm px-2 md:px-4">
                <h2 className="text-xl font-bold text-primary flex items-center justify-center gap-1 mb-3 md:text-2xl md:mb-4">
                  {v.title}
                </h2>
                <p className="italic text-base text-custom-blue opacity-70 mb-3 font-medium md:text-lg">
                  {v.sub_title}
                </p>
                <p className="text-custom-blue text-base font-normal leading-relaxed md:text-lg md:leading-relaxed">
                  {v.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ScrollCardSectionEasyFollow;
